import React,{useState, useEffect, useRef} from 'react'
import { OrderKind } from '../../utils/enums';
import { campaignTargetsCustomerClient, voucherValueFor } from '../../utils/campaignTargeting';
import CartProductComponent from './cartProductComponent';
import { useNavigate } from 'react-router-dom';
import { inject, observer } from "mobx-react";
import DeleteAccount from '../../modals/deleteAccount';
import _useTranslate from '../../hooks/_useTranslate';
import GreenSubmitButton from "../../buttons/greenSubmitButton";
import Colors from '../../assets/colors';
import {API_URL} from "../../assets/globalConstants";
import SmallAlert from '../../modals/smallAlert';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { DateTime } from "luxon";
import CouverModal from './couverModal';

const CartTable = ({feathersStore})=> {

  let navigate = useNavigate();

  let common = _useTranslate(feathersStore.language, "common");  
  let cart = _useTranslate(feathersStore.language, "cart");  

  const [total, setTotal] = useState(0.0);
  const [cart_products, setCart_products] = useState([]);
  const [itemToDelete, setItemToDelete] = useState({});
  const [voucherText, setVoucherText] = useState("");
  const [warning, setWarning] = useState(false);
  const [minimumOrder, setMinimumOrder] = useState(false);
  const [couver, setCouver] = useState(false);
  const [couverQuantity, setCouverQuantity] = useState(1);
  const [couverFail, setCouverFail] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [minimumVoucher, setMinimumVoucher] = useState(false);
  const [minimumVoucherText, setMinimumVoucherText] = useState(null);
  const [voucherError, setVoucherError] = useState(null);   //why the code was refused; null = nothing to say

  //Modal Controls
  const [visible, setVisible] = useState(false);
  
  const discountsArrayRef = useRef([]);

  //The store is the authority on what is applied. A logout (or a committed order) empties it, but
  //this screen kept its own copy in a ref, so a discount could outlive the identity that earned it
  //and ride into the next checkout. Follow the store down to empty.
  useEffect(()=> {
    if(feathersStore.discountsArray?.length === 0 && discountsArrayRef.current?.length > 0){
      discountsArrayRef.current = [];
      setDiscount(0);
    }
  },[feathersStore.discountsArray]);

  //Re-price the applied code whenever the basket changes. The figure shown here used to be the one
  //frozen when the code was typed, so it disagreed with checkout the moment a quantity was touched -
  //and it kept a minimum-order discount on a basket that had dropped below the minimum.
  useEffect(()=> {
    const row = discountsArrayRef.current?.[0];
    if(!row) return;
    const value = voucherValueFor(row, total);
    discountsArrayRef.current = [{...row, value}];
    setDiscount(value);
  },[total]);

  useEffect(()=> { //used to empty cart if there are forgotten items after QR code scan
    // A LIVE in-memory table session is authoritative: losing the localStorage key mid-session
    // (6h TTL lapse, iOS ITP purge, a second tab) must not downgrade a seated customer to the
    // eshop checkout ("it asks for my name at the table" incident) or wipe their cart. The
    // expiry purge below only applies when the store itself is not in table mode — a true
    // session end (a reload after expiry lands here as eshop via the constructor rehydrate).
    const stored = readStoredTableId();
    if(stored.status === 'valid'){
      feathersStore.setTableId(stored.value);
    }else if(!feathersStore.liveTableSessionValid()){
      if(stored.status === 'expired'){
        localStorage.removeItem('tableId');
        emptyCart();
      }
      feathersStore.setTableId('eshop');
    }
    feathersStore.touchTableSession(); //sliding 6h TTL while the table session is active
  },[]);

  const readStoredTableId = () => {
    const itemStr = localStorage.getItem('tableId');
    if(!itemStr) return { status: 'missing', value: null };
    try{
      const item = JSON.parse(itemStr);
      if(new Date().getTime() > item.expiry) return { status: 'expired', value: null };
      return { status: 'valid', value: item.value };
    }catch(e){ return { status: 'missing', value: null }; }
  }

  const emptyCart = () => {   
    localStorage.setItem("fdwa_cart", JSON.stringify([]));
    feathersStore.resetOrderItem();
    feathersStore.setCartLength(0);
  }

  useEffect(() => {
    const cartFromStorage = JSON.parse(localStorage.getItem('fdwa_cart'))  
    if(cartFromStorage){
    setCart_products(cartFromStorage); 
    setTotal(cartFromStorage.map(element => element.product_totalPrice)
    .reduce((t, sum) => sum + t, 0))  
    }
  }, []);

  useEffect(() => {
    const asyncFn = async() => {
    if(
      (feathersStore?.tableId !== "eshop") && feathersStore.settings?.couver)
      cart_products?.length > 0 && await checkForCouver();
    }
    asyncFn();
  }, [feathersStore?.tableId, feathersStore?.settings, cart_products]);

  const updateTotalAmount = clonedCartProducts => {
    setTotal(clonedCartProducts.map(element => element.product_totalPrice)
      .reduce((t, sum) => sum + t, 0));
    feathersStore.setCartLength(clonedCartProducts.length);   
  };


  const goToProduct = async (item) => {
    const index = cart_products.indexOf(item);
    const otherData = {
      id: item.id,
      quantity: item.product_quantity,
      sizePrice: 0,
      total: item.product_totalPrice,     
      extraSum: item.product_extraSum,
      extraItems: [...item.features], //extraItems is written wrongly as features
      ingredientItems: [...item.ingredients], //the same
      attributeItems: [...item.attributes],
      fromCart: true,
      cartIndex: index,
      comments: item.product_comments,
      printers: item.product_printers,
      blockOtherPrinters: item.product_blockOtherPrinters || false,
      kdsSections: item.product_kdsSections,
      stock: item.product_stock,
      handleStock: item.product_handleStock,
      weightedPrice: item.product_weightedPrice,
      weightedQuantity: item.product_weightedQuantity,
     }

    const prod = await feathersStore.getProduct(item.product_id);    
    prod.features.forEach(feature => Object.assign(feature, {picked: false}));
    Object.assign(prod, otherData);
    if(prod?.sizes?.length > 0)
    for (var i in prod.sizes) {
      if (prod.sizes[i].default === true) {
        prod.sizes[i].default = false;
      }
      for(var k in prod.sizes){
        if(prod?.sizes[k]?.name === item.product_size?.name || feathersStore._translateNameAttr(prod.sizes[k]) === feathersStore._translateNameAttr(item.product_size)){
          prod.sizes[k].default = true;
          prod.sizePrice = prod.sizes[k][packagePricing() ? 'packagePrice' : 'price'];
        }
      } 
    };
    prod && navigate(`/product/${encodeURIComponent(feathersStore._translateName(prod))}`,{
        state: {id: `${prod._id}`,  payload: prod}     
    }); 
  };  

  const packagePricing = () => {
    return  feathersStore.settings?.deliveryPricing && feathersStore?.tableId === 'eshop'
      && !feathersStore.qrReadOnly();
  }
  
  const onPressToggle = (item, toggle) => async () => {
    const clonedItem = JSON.parse(JSON.stringify(item))
    let clonedCartProducts = [...cart_products];
    const index = cart_products.indexOf(item);
    let quantity = +clonedItem.product_quantity;
    const itemPrice = clonedItem.product_totalPrice / quantity;
    if(toggle === "plus"){
      // Fetch live stock from server — cart items can sit for a long time while
      // other users reduce the actual stock, so product_stock snapshot is not reliable.
      const liveProduct = await feathersStore.getProduct(item.product_id);
      const livePickedSize = liveProduct?.sizes?.find(s => s.name === item.product_size?.name);
      const liveStock = feathersStore.effectiveStock(liveProduct, livePickedSize);
      if (feathersStore.checkOutOfStock(item.product_id, item.product_handleStock, liveStock,
            1, clonedCartProducts, item.product_weightedQuantity)){
        setWarning(true); return;
      }
      quantity +=1;
    }
    else if(quantity > 0){
      if(item.product_id === feathersStore.settings?.couverId && quantity <= 1) return;
      quantity -=1;
    }
    if (quantity !== 0){
      clonedItem.product_quantity =  quantity;    
      clonedItem.product_totalPrice = itemPrice * quantity;
      clonedCartProducts.splice(index, 1, clonedItem); 
      setCart_products(clonedCartProducts);     
      localStorage.setItem("fdwa_cart", JSON.stringify(clonedCartProducts));     
      updateTotalAmount(clonedCartProducts);        
    }else {
      setVisible(true);
      setItemToDelete(item)
    }
  }

  const deleteItem = () => {
    if(itemToDelete.product_id === feathersStore.settings?.couverId){
      setVisible(false);
      return;
    }
    const index = cart_products.indexOf(itemToDelete);
    let clonedCartProducts = [...cart_products];
    clonedCartProducts.splice(index, 1); 
    setCart_products(clonedCartProducts);     
    localStorage.setItem("fdwa_cart", JSON.stringify(clonedCartProducts));     
    updateTotalAmount(clonedCartProducts);
    setVisible(!visible);  
    if(
      (feathersStore?.tableId !== 'eshop') 
        && feathersStore.settings?.couver
        && (itemToDelete.product_id === feathersStore.settings?.couverId)
      ){     
        setCouver(true);
        setCouverQuantity(1);
      }  
  }

  const closeConfirm = ()=> {
    setVisible(!visible);
  }

  const calculateTotalQuantity = () => {
    return cart_products.length !== 0? 
    cart_products.map(el => el.product_quantity)
    .reduce((q, sum) => sum + q, 0) : 0
  }

  const goToCheckout = async() => {
    if (feathersStore.settings?.minimumOrder && (+total < +feathersStore.settings.minimumOrder)){
      setMinimumOrder(true); return;
    }   
    if(discount){
      feathersStore.setDiscountsArray(discountsArrayRef.current);
    }
    navigate('/checkout', {state: {payload: discountsArrayRef.current}});
  }

  const checkForCouver = async() => {
    const couverId = feathersStore.settings?.couverId
    try{
      const table = await feathersStore.getTable(feathersStore.tableId);
      if(!table) return; //feathers client not connected yet - re-runs on the next dep change; the Checkout send gate is the backstop
      // A booking PRE-ORDER is built independently of whatever order sits on the (possibly occupied)
      // table, so the occupants' couver must NOT satisfy this check - the pre-order needs its own.
      const isPreOrder = feathersStore.preOrderBooking?.tableId === feathersStore.tableId;
      const tableCouvers = isPreOrder ? [] : table?.order?.items?.filter(item => item.product_id === couverId);
      const cartCouvers =  cart_products.filter(item => item.product_id === couverId);
      if(
        (!tableCouvers || tableCouvers?.length < 1) &&
        (cartCouvers?.length < 1)){
          if(table?.couverOnce){
            try{
              await addCouverToCart(1);
            }catch(err){
              // Broken couver product (deleted couverId, missing sizes...): the swallowing catch below
              // used to hide this and the send gate then fail-closed into a silent cart<->checkout
              // bounce. Tell the customer instead.
              console.log('Couver auto-add failed:', err);
              setCouverFail(true);
            }
          }else{
            setCouver(true);
          }
        }else return;
    }catch(e){ /* transient (e.g. socket hiccup) - previously killed the check silently; now retried on the next tableId/settings/cart change, and the Checkout send gate guarantees no couver-less send */ }
  }

  const couverQuantityChange = event => {    
    setCouverQuantity(event.target.value);
  };

  const voucherChange = event => {    
    setVoucherText(event.target.value);
  };

  const applyVoucher = async() => {
    //A typed code REPLACES whatever was applied before. This used to bail out whenever a
    //voucher was already in the cart, so switching from one code to another silently did
    //nothing and the ORIGINAL row stayed - including a first-order row the customer no
    //longer qualified for, which the server then refused at checkout.
    if(voucherText?.length > 0 ){
      //Every refusal path used to end in silence — a wrong code, an expired one, a code aimed at
      //another channel, and now a first-order code the customer is not entitled to all did nothing
      //at all, which reads as a broken button. Track WHY and say so.
      let applied = false;
      let reason = 'voucherInvalid';
      const now = DateTime.now().setZone(feathersStore.timezone());
      const _vouchers = await feathersStore.coupons();
      //A voucher is a campaign too, so it honours the same targeting. In the cart the customer has
      //not chosen delivery vs takeaway yet, so pass the SET of kinds this order could still become -
      //the checkout re-checks with the resolved kind.
      const kinds = feathersStore.isTableOrder()
        ? [OrderKind.webTable]
        : [OrderKind.webDelivery, OrderKind.webTakeAway];
      const vouchers = _vouchers?.filter(coupon => coupon?.general
        && campaignTargetsCustomerClient(coupon, kinds))
      for(let voucher of vouchers){
        if(voucher.code === voucherText){   
          let valid = false;
          if(voucher?.endDate){
            if(DateTime.fromISO(voucher.endDate) > now)valid = true;
          }else{
            if(DateTime.fromMillis(voucher.createdAt).plus({days : voucher.duration}) > now)valid = true;
          }
          if(!valid){ reason = 'voucherExpired'; continue; }
          {
            //A firstOrderOnly campaign is a TYPED CODE the shop advertises, so the check has to live
            //here as well as on the assigned-coupon rail - otherwise the code works on every order.
            //The client gate only avoids offering it; the server hook is the authority.
            if(voucher.firstOrderOnly && !(await feathersStore.isFirstOrderEligible())){
              reason = 'voucherFirstOrderOnly';
              continue;
            }
            if( total >= (voucher?.minimumOrder || 0)){
              if(voucher.type === "Percent"){  
                applied = true;
                const disc = parse_fix(total * voucher.reward / 100);
                setDiscount(parseFloat(disc));
                discountsArrayRef.current = [{
                  type: "Percent",
                  label: voucher.firstOrderOnly ? "FirstOrder" : "General",
                  couponId: voucher._id,   //the server re-reads the campaign by this id
                  value: disc,
                  reward: voucher.reward,
                  minimumOrder: +(voucher.minimumOrder || 0),   //carried so checkout can re-check it - see voucherValueFor
                  applied: false,
                  display: voucher.display,
                  promotionText: voucher.promotionText
                }]
              }else if(voucher.type === "Cash"){  
                applied = true;
                setDiscount(parseFloat(voucher.reward));
                discountsArrayRef.current = [{
                  type: "Cash",
                  label: voucher.firstOrderOnly ? "FirstOrder" : "General",
                  couponId: voucher._id,   //the server re-reads the campaign by this id
                  value: parseFloat(voucher.reward),
                  reward: voucher.reward,
                  minimumOrder: +(voucher.minimumOrder || 0),   //carried so checkout can re-check it - see voucherValueFor
                  applied: false,
                  display: voucher.display,
                  promotionText: voucher.promotionText  
                }]
              }
            }else{
              setMinimumVoucherText(voucher.minimumOrder)
              setMinimumVoucher(true);
              applied = true;   //this one has its own, more specific modal
            }
          }
        }
      }  
      if(!applied) setVoucherError(reason);
    }  
  }

  const addCouverToCart = async(qty = couverQuantity) => {
    const item = await feathersStore.getProduct(feathersStore.settings?.couverId);
    let cart = [];
    let cartBiggestId = 0;
    if(localStorage.getItem('fdwa_cart')){
      cart = JSON.parse(localStorage.getItem('fdwa_cart'));
      if(cart.length > 0)
        cartBiggestId = +cart.map(e => e.id).sort().reverse()[0] + 1;
    }
    const pickedSize = item.sizes.filter(size => size.default)[0];
    pickedSize && Object.assign(pickedSize, {nameTranslated: feathersStore._translateNameAttr(pickedSize)});
    const cartItem = {
      id: cartBiggestId,
      product_id: item._id,
      product_name: item.name,
      product_nameTranslated: feathersStore._translateName(item),
      product_size: pickedSize,
      product_image: `${API_URL}/images/${item?.images[0]}`,
      product_price: +item['retailPrice'],
      product_totalPrice: +item.retailPrice * qty,
      product_netPrice: calculateNetPrice(item),
      product_quantity: +qty,
      product_extraSum: 0,
      features: [],
      ingredients: [],
      attributes: [],  
      product_printers: item.printers,
      product_blockOtherPrinters: item.blockOtherPrinters || false,
      product_kdsSections: item.kdsSections,
      product_vat: item.vat,
      ...feathersStore.lineMarker(false), //couver: dine-in priced, plain vat
      product_stock: feathersStore.effectiveStock(item, pickedSize),
      product_handleStock: item.handleStock,
      product_comments: item.comments,
      product_recipes: pickedSize?.recipes?.length ? pickedSize.recipes : (item.recipes ?? []),
      isCommodity: item.isCommodity,
      nameTranslations: item?.nameTranslations,
      shortDescriptionTranslations: item?.shortDescriptionTranslations
    }
    if (feathersStore.checkOutOfStock(item._id, item.handleStock, feathersStore.effectiveStock(item, pickedSize), cartItem.product_quantity, cart)){
      setWarning(true); return;
    } 
    let clonedCartProducts = [...cart_products];
    clonedCartProducts.push(cartItem); 
    setCart_products(clonedCartProducts);     
    localStorage.setItem("fdwa_cart", JSON.stringify(clonedCartProducts));     
    updateTotalAmount(clonedCartProducts);
    setCouver(false);
  }

  const calculateNetPrice = item => {
    const label = feathersStore.settings.vats?.find(v => +v.id === +item.vat)?.label
    return +((item.retailPrice) / (1 + label / 100 )).toFixed(4);
  }

  const goToHome = () => {
    navigate('/');
  }

  const constructTitle = (item)=> {  
    return feathersStore._translate(item.product_name , item.product_nameTranslated) 
      + (item?.product_size?.name ? 
        "  " + feathersStore. _translateNameAttr(item.product_size) :
        "");   
  }  

  const constructData = item => {
    return (item?.product_weightedQuantity > 0 ? ((+item.product_weightedQuantity * 1000).toString() + " " + common.grams + " ") : "")
    + item.features?.map(feature => feathersStore._translateNameAttr(feature) + "  ")
    + item.attributes?.map(attribute => "  " + feathersStore._translateTitle(attribute) + ": " 
        + feathersStore._translateNameAttr(attribute) + " " + (attribute?.quantity > 1 ? "x" + attribute.quantity + " " : ""))
    + (item?.ingredients?.length > 0 ? ("  " + cart.without  + item.ingredients?.map(ingredient => "  " 
        + feathersStore._translateNameAttr(ingredient))) : "")
  } 

  const parse_fix = (price) => {
    if(!price)price = 0;
    return price.toFixed(2);
  } 

  const renderCartProducts = cart_products.map((item, index) => (
    <CartProductComponent
      key={index}
      image={item.product_image}
      quantity={item.product_quantity}
      title={constructTitle(item)}
      data={constructData(item)}
      price={item.product_totalPrice}
      handleClick={() => goToProduct(item)}
      onClickPlus={onPressToggle(item, "plus")}
      onClickMinus={onPressToggle(item, "minus")}
      disableMinus={item.product_id === feathersStore.settings?.couverId && item.product_quantity <= 1}
      recipes={item.product_recipes}
    />
  ))

  return(
  <>
    <>
      <DeleteAccount
        show={visible}
        heading={common.removeItem}
        onDelete={deleteItem}
        onClose={closeConfirm}
        message={common.youSure}
        no={common.noSmall}
        yes={common.yesSmall}
        fromCart={true}
      />       
    </>
    <div className="table-container">     
      <div className="table-body">
        <div className="section-header">{cart.cartItems}<div className="section-line"></div></div>        
        {renderCartProducts} 
        <div className="table-footer"> 
          <div className="coupon-caption">{cart.couponCaption}</div>
          <div className="coupon-input-container"> 
            <input aria-label='coupon' type="text" placeholder={cart.couponCode} className="coupon-input"
              value={voucherText}
              onChange={voucherChange}
            />
            <span className="coupon-button"  onClick={applyVoucher}>
              <ArrowForwardIcon/>
            </span>   
          </div>             
        </div>  
      </div> 
      <div className="cart-total-table">
        <div className="section-header">{cart.cartTotals}<div className="section-line"></div></div>        
        <div className="cart-total-body">
          <div className="totals-line">{common.products}: {feathersStore.cartLength}</div>
          <div className="totals-line">{common.items}: {calculateTotalQuantity()}</div>
          <div className="totals-line">{cart.subTotal}: {feathersStore.formatMoney((+total).toFixed(2))}</div>
          <div className="totals-line">{cart.couponDiscount}: {feathersStore.formatMoney(parse_fix(discount))}</div>
        </div>
        <div className="totals-line">{common.total}: {feathersStore.formatMoney((total - discount).toFixed(2))}</div>
        <div className="cart-total-footer">        
          <GreenSubmitButton aria-labelledby='Complete order'
            disabled={total === 0 || !feathersStore.restaurantOpen || couver}
            color={undefined}
            title={cart.completeOrder}
            onClick={goToCheckout}
            width="100%"
          ></GreenSubmitButton>         
          <GreenSubmitButton aria-labelledby='Complete order' 
            color={undefined}
            title={cart.continueShopping} 
            onClick={goToHome} 
            width="100%"
            variant="outlined"
          ></GreenSubmitButton>       
        </div>
      </div>   
    </div>   
    <SmallAlert 
      show={warning} 
      mode="stock"
      heading={common.stockWarningHeader} 
      onClose={() => setWarning(false)}  
      message={common.stockWarning}
    />
    <SmallAlert 
      show={minimumOrder} 
      mode="stock"
      heading={common.minimumOrderHeader} 
      onClose={() => setMinimumOrder(false)}  
      message={`${common.minimumOrder}${feathersStore.formatMoney(feathersStore?.settings.minimumOrder)}`}
    /> 
    <CouverModal
      show={couver}
      couverQuantity={couverQuantity}
      couverQuantityChange={couverQuantityChange}
      addCouverToCart={addCouverToCart}
    />
    <SmallAlert
      show={couverFail}
      mode="stock"
      heading={cart.couverUnavailableHeader}
      onClose={() => setCouverFail(false)}
      message={cart.couverUnavailable}
    />
    <SmallAlert
      show={!!voucherError}
      mode="stock"
      heading={common.voucherNotApplied}
      onClose={() => setVoucherError(null)}
      message={common[voucherError] || common.voucherInvalid}
    />
    <SmallAlert 
      show={minimumVoucher} 
      mode="stock"
      heading={common.minimumVoucherOrder} 
      onClose={() => setMinimumVoucher(false)}  
      message={`${common.minimumVoucherCaption}: ${feathersStore.formatMoney(minimumVoucherText)}`}
    />       
  </>
  )
}

export default inject('feathersStore')(observer(CartTable));